/**
 * 收貨狀態 Receipt Status — REFERENCED lens (S4.3).
 *
 * Visibly read-only projection of the shipping store: NO action column,
 * every row carries a `SourceStampTag` (來源:船務 + syncedAt + drill-through
 * to 進貨 Goods Receipt, where all posting write-back lives).
 */

import { Table, Tag, theme } from 'antd';
import type { TableProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import SourceStampTag from '@/components/common/SourceStampTag';
import { useOccReceiptStatus } from '@/hooks/useOcc';
import type { OccReceiptStatusRow } from '@/lib/occ/selectors';
import type { GoodsReceiptStatus } from '@/types/api/shipping';
import { LensFooter, LensUnavailableNotice, ReadOnlyBanner } from '../lensShared';

const STATUS_TAG_COLOR: Record<GoodsReceiptStatus, string> = {
  draft: 'default',
  posted: 'green',
};

const ReceiptStatusLens = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['occ', 'common']);
  const { token } = theme.useToken();
  const { rows, syncedAt, isLoading, error, refresh, unavailable } = useOccReceiptStatus();

  const columns: TableProps<OccReceiptStatusRow>['columns'] = [
    {
      title: t('occ:occ.receiptStatus.columns.receipt'),
      key: 'receipt',
      width: 160,
      render: (_: unknown, row) => (
        <span style={{ fontWeight: token.fontWeightStrong }}>{row.grNumber}</span>
      ),
    },
    {
      title: t('occ:occ.receiptStatus.columns.po'),
      dataIndex: 'poNumber',
      key: 'poNumber',
      width: 160,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('occ:occ.receiptStatus.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: GoodsReceiptStatus) => (
        <Tag color={STATUS_TAG_COLOR[status]} style={{ margin: 0 }}>
          {t(`occ:occ.receiptStatus.status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('occ:occ.receiptStatus.columns.receivedAt'),
      dataIndex: 'receivedAt',
      key: 'receivedAt',
      width: 150,
      render: (value: string | null) =>
        renderDash(value ? dayjs(value).format('YYYY-MM-DD HH:mm') : null),
    },
    {
      title: t('occ:occ.receiptStatus.columns.lines'),
      dataIndex: 'lineCount',
      key: 'lineCount',
      width: 80,
      align: 'right',
      render: (value: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>,
    },
    {
      title: t('occ:occ.receiptStatus.columns.qtyReceived'),
      dataIndex: 'qtyReceived',
      key: 'qtyReceived',
      width: 110,
      align: 'right',
      render: (value: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>,
    },
    {
      title: t('occ:occ.receiptStatus.columns.qtyDamaged'),
      key: 'qtyDamaged',
      width: 110,
      align: 'right',
      render: (_: unknown, row) =>
        row.qtyDamaged > 0 ? (
          <span style={{ color: token.colorError, fontVariantNumeric: 'tabular-nums' }}>
            {row.qtyDamaged}
          </span>
        ) : (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>0</span>
        ),
    },
    {
      // Referenced contract: SourceStampTag on EVERY row, no action column.
      title: t('occ:occ.receiptStatus.columns.source'),
      key: 'source',
      width: 200,
      render: (_: unknown, row) => (
        <SourceStampTag
          sourceLabel={t('occ:occ.receiptStatus.sourceLabel')}
          syncedAt={row.syncedAt}
          drillLabel={t('occ:occ.receiptStatus.drill')}
          onDrill={() => navigate('/auth/goods-receipts')}
        />
      ),
    },
  ];

  return (
    <>
      <LensUnavailableNotice active={unavailable} domain="receiptStatus" />
      <ReadOnlyBanner note={t('occ:occ.receiptStatus.readOnlyNote')} />
      <TableContainer
        errorState={{
          active: Boolean(error),
          title: t('occ:occ.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<OccReceiptStatusRow>
          rowKey="receiptId"
          dataSource={rows}
          columns={columns}
          loading={isLoading}
          locale={{ emptyText: <ListEmptyState title={t('occ:occ.states.noReceipts')} /> }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1080 }}
        />
      </TableContainer>
      <LensFooter syncedAt={syncedAt} />
    </>
  );
};

export default ReceiptStatusLens;
